import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";

// Documents checklist modal state + handlers
export const useDocumentChecklist = ({ language, text, setEmployees, setError }) => {
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false); // Track if saving is in progress
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
  const [documentChecklist, setDocumentChecklist] = useState({
    photosCount: false,
    birthCertificateCopy: false,
    gradCertificateOriginal: false,
    idCopy: false,
    noCrimeCertificate: false,
    laborLetterOriginal: false,
    medicalCheckForm111: false,
  });

  const handleViewDocuments = async (employee) => {
    try {
      console.log('📄 Opening documents for employee:', employee);
      setLoadingDocuments(true);
      setSelectedEmployeeDocuments(employee);
      setHasUnsavedChanges(false); // Reset unsaved changes flag

      // Initialize document checklist from employee data
      const empData = employee.fullEmployeeData || {};
      console.log('📋 Employee document data:', empData);

      const checklistData = {
        photosCount: empData.photosCount === 1 || empData.photosCount === true, // Handle both int and boolean
        birthCertificateCopy: empData.birthCertificateCopy || false,
        gradCertificateOriginal: empData.gradCertificateOriginal || false,
        idCopy: empData.idCopy || false,
        noCrimeCertificate: empData.noCrimeCertificate || false,
        laborLetterOriginal: empData.laborLetterOriginal || false,
        medicalCheckForm111: empData.medicalCheckForm111 || false,
      };

      console.log('✅ Setting document checklist:', checklistData);
      setDocumentChecklist(checklistData);
      setShowDocumentsModal(true);
    } catch (err) {
      console.error("❌ Failed to load documents:", err);
      setError(text[language].failedToFetch + " documents");
    } finally {
      setLoadingDocuments(false);
    }
  };

  // Handler to update document checklist
  // Update checkbox state without saving
  const handleDocumentCheckboxChange = (documentField, value) => {
    console.log(`📝 Checkbox changed: ${documentField} = ${value}`);

    // Update local state
    setDocumentChecklist(prev => ({
      ...prev,
      [documentField]: value
    }));

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
  };

  // Save all document changes to database
  const handleSaveDocuments = async () => {
    try {
      console.log(`💾 Saving all documents for employee ${selectedEmployeeDocuments?.id}`);

      // Set saving state
      setSavingDocument(true);

      // Prepare payload with all document fields - convert booleans to integers for photosCount
      const payload = {
        photosCount: documentChecklist.photosCount ? true : false,
        birthCertificateCopy: documentChecklist.birthCertificateCopy,
        gradCertificateOriginal: documentChecklist.gradCertificateOriginal,
        idCopy: documentChecklist.idCopy,
        noCrimeCertificate: documentChecklist.noCrimeCertificate,
        laborLetterOriginal: documentChecklist.laborLetterOriginal,
        medicalCheckForm111: documentChecklist.medicalCheckForm111,
      };

      console.log('🚀 Sending update to API:', payload);
      console.log('🌐 API URL:', `${API_URL}/employees/${selectedEmployeeDocuments.id}`);

      // Make API call to update employee documents
      const response = await apiFetch(
        `${API_URL}/employees/${selectedEmployeeDocuments.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      // Try to get error details from response
      let errorData;
      try {
        errorData = await response.json();
        console.log('📡 Response data:', errorData);
      } catch (e) {
        console.log('⚠️ Could not parse response as JSON');
      }

      if (!response.ok) {
        const errorMessage = errorData?.message || errorData?.error || `Server error: ${response.status}`;
        console.error('❌ Server error:', errorMessage);
        throw new Error(errorMessage);
      }

      console.log('✅ Documents saved successfully');

      // Update the employee data in state
      setEmployees(prev => prev.map(emp =>
        emp.id === selectedEmployeeDocuments.id
          ? { ...emp, fullEmployeeData: { ...emp.fullEmployeeData, ...payload } }
          : emp
      ));

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);
      setSavingDocument(false);

    } catch (err) {
      console.error("❌ Failed to save documents:", err);
      console.error("❌ Error details:", err.message);
      setSavingDocument(false);
      setError(`${text[language].failedToUpdateDocuments}: ${err.message}`);
    }
  };

  return {
    showDocumentsModal,
    setShowDocumentsModal,
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,
    setHasUnsavedChanges,
    loadingDocuments,
    savingDocument,
    hasUnsavedChanges,
    documentChecklist,
    setDocumentChecklist,
    handleViewDocuments,
    handleDocumentCheckboxChange,
    handleSaveDocuments,
  };
};
